'use client';
import Gun from 'gun/gun';
import 'gun/lib/radix';
import 'gun/lib/radisk';
import 'gun/lib/store';
import 'gun/lib/rindexed';
import SEA from 'gun/sea';
Gun.SEA = SEA;
import { Base64 } from 'js-base64';

import type { IGunChain, IGunInstance } from 'gun';
import { hashMessage, Hex, recoverMessageAddress } from 'viem';
import {
  GUN_ENV_NAMESPACE_KEY,
  GUN_PEER_URL,
  SIGN_IN_MESSAGE,
} from './constants';
import { TGunUserNode, GunNodeKey, IDdoc } from '@/types';
import { toast } from '@fileverse/ui';
import { WorkerInstance } from './worker-utils/worker-instance';

export const Sea = Gun.SEA;

export const ENCRYPTED_DDOC_KEYS: Array<Partial<keyof IDdoc>> = [
  'linkKey',
  'lastPublishedContent',
  'content',
  'linkKeyNonce',
  'commentKey',
];
class GunInstance {
  public gun: IGunInstance;
  constructor() {
    this.gun = Gun({
      peers: GUN_PEER_URL.split(', '),
      localStorage: false,
      radisk: true,
      retry: Infinity,
    });
  }
}
export const isNullish = (value: any) => value === null || value === undefined;

export const gunInstance = new GunInstance().gun;

export const getGunUserNode = () => {
  return { userNode: gunInstance.user() };
};

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const errorAfter = async (ms: number) => {
  await delay(ms);
  throw new Error('timed out');
};

export const waitFor = async (promise: Promise<any>, ms: number) => {
  return Promise.race([promise, errorAfter(ms)]);
};

export const base64ToJSON = (data: string) => JSON.parse(Base64.decode(data));

export const jsonToBase64 = (data: any) => Base64.encode(JSON.stringify(data));

export async function waitForGunData(
  node: IGunChain<any, IGunInstance<any>, IGunInstance<any>, any>,
  timeout = 5000
) {
  let sub: any;
  const listner = new Promise((resolve) => {
    node.on((data, _key, _message, event) => {
      sub = event;
      if (!isNullish(data)) {
        sub?.off();
        resolve(data);
      }
    });
  }).finally(() => {
    sub?.off();
  });

  return await waitFor(listner, timeout);
}

export async function checkUserExists(userAlias: string) {
  const key = '~@' + userAlias;
  try {
    const data = await waitForGunData(gunInstance.get(key));
    return !!data;
  } catch (err) {
    console.error(err);
    return false;
  }
}

const getGunNodeDataWithRetry = async (
  parentNodeKey: string,
  childNodeKey: string
): Promise<string | null> => {
  let retryCount = 0;
  while (retryCount < 3) {
    try {
      const data = await waitForGunData(
        gunInstance.get(parentNodeKey).get(childNodeKey),
        10000 // 10 seconds
      );
      if (data) return data;
    } finally {
      retryCount++;
    }
  }
  return null;
};

async function getAliasNodeValues(userAlias: string) {
  const key = '~@' + userAlias;
  try {
    const data = await waitForGunData(gunInstance.get(key));
    for (const key in data) {
      if (key === '_') continue;
      const parentNodeKey = data[key]['#'];
      if (!parentNodeKey) continue;
      const authData = await getGunNodeDataWithRetry(parentNodeKey, 'auth');
      const pubData = await getGunNodeDataWithRetry(parentNodeKey, 'pub');
      const epubData = await getGunNodeDataWithRetry(parentNodeKey, 'epub');

      // if all data is found break out of the loop
      if (authData && pubData && epubData) break;
    }
  } catch (err) {
    console.log(err);
  }
}

export const getAuthenticatedUserNode = async (
  signedMsg: Hex,
  isLegacy: boolean
) => {
  const userWalletAddress = await recoverMessageAddress({
    signature: signedMsg,
    message: isLegacy ? hashMessage(SIGN_IN_MESSAGE) : SIGN_IN_MESSAGE,
  });
  // logout any existing user to avoid conflicts
  gunInstance.user().leave();
  const userExists = await checkUserExists(userWalletAddress);
  if (userExists) return await loginWithGun(userWalletAddress, signedMsg);
  return await createUserAndLogin(userWalletAddress, signedMsg);
};

const createUserAndLogin = async (
  userAlias: string,
  password: Hex
): Promise<{ node: TGunUserNode } | null> => {
  const userNode = gunInstance.user();
  return new Promise((resolve, reject) => {
    userNode.create(userAlias, password, async (ack: any) => {
      if ('err' in ack) {
        console.log('Error creating user', ack.err);
        reject(null);
      } else {
        const loggedInUser = await loginWithGun(userAlias, password);
        if (!loggedInUser) return reject(null);
        resolve(loggedInUser);
      }
    });
  });
};

const loginWithGun = async (
  userAlias: string,
  password: Hex
): Promise<{ node: TGunUserNode } | null> => {
  const userNode = gunInstance.user();
  await getAliasNodeValues(userAlias);
  return new Promise((resolve, reject) => {
    // @ts-ignore
    if (userNode._.sea) {
      // Check for sea pair instead of userNode.is
      resolve({ node: userNode });
      return;
    }
    userNode.auth(userAlias, password, (ack: any) => {
      if ('err' in ack) {
        console.log('Error logging in ', ack.err);
        reject(null);
      } else {
        resolve({ node: userNode });
      }
    });
  });
};

export const getGunPortalKeys = async (userNode: TGunUserNode) => {
  try {
    const portalKeyData = await waitForGunData(
      userNode
        .get(GUN_ENV_NAMESPACE_KEY)
        .get(GunNodeKey.Ddoc)
        .get(GunNodeKey.Onchain)
        .get(GunNodeKey.Key),
      10000 // 10 seconds
    );
    // @ts-ignore
    const userSeaPair = userNode._.sea;
    const decryptedPortalData = await Sea.decrypt(portalKeyData, userSeaPair);
    return base64ToJSON(decryptedPortalData);
  } catch (e) {
    console.log(e);
    return null;
  }
};

export const getDdocOnGunById = async (
  userNode: TGunUserNode,
  ddocId: string
): Promise<IDdoc | null> => {
  try {
    const docItem: IDdoc = await waitForGunData(
      userNode
        .get(GUN_ENV_NAMESPACE_KEY)
        .get(GunNodeKey.Ddoc)
        .get(GunNodeKey.Files)
        .get(ddocId)
        .get(GunNodeKey.FileData)
    );
    if (!docItem) return null;
    const data = {} as any;
    for (const key in docItem) {
      if (key === '_') continue;
      if (ENCRYPTED_DDOC_KEYS.includes(key as keyof IDdoc)) {
        const decryptedData = !isNullish(docItem[key as keyof IDdoc])
          ? //  @ts-ignore
            await Sea.decrypt(docItem[key], userNode._.sea)
          : null;
        data[key] = decryptedData;
      } else {
        data[key] = docItem[key as keyof IDdoc];
      }
    }
    return data;
  } catch (err) {
    return null;
  }
};

// Temporary function once tested will replace getDdocOnGunById with this one
export const getDdocOnGunByIdViaWorker = async (
  userNode: TGunUserNode,
  ddocId: string
): Promise<IDdoc | null> => {
  try {
    const docItem: IDdoc = await waitForGunData(
      userNode
        .get(GUN_ENV_NAMESPACE_KEY)
        .get(GunNodeKey.Ddoc)
        .get(GunNodeKey.Files)
        .get(ddocId)
        .get(GunNodeKey.FileData)
    );
    if (!docItem) return null;

    const decryptedData = await WorkerInstance.decryptDdocItem(
      docItem,
      // @ts-ignore
      userNode._.sea
    );

    return decryptedData as IDdoc;
  } catch (err) {
    return null;
  }
};

// get raw ddoc data from gun without decrypting it
export const getRawDdocOnGunById = async (
  userNode: TGunUserNode,
  ddocId: string,
  timeout = 5000
): Promise<IDdoc | null> => {
  let data = {} as any;
  try {
    const docItem: IDdoc = await waitForGunData(
      userNode
        .get(GUN_ENV_NAMESPACE_KEY)
        .get(GunNodeKey.Ddoc)
        .get(GunNodeKey.Files)
        .get(ddocId)
        .get(GunNodeKey.FileData),
      timeout
    );
    if (!docItem) return null;

    for (const key in docItem) {
      if (key === '_') continue;
      data[key] = docItem[key as keyof IDdoc];
    }
    return data;
  } catch (err) {
    return null;
  } finally {
    data = null;
  }
};

export const putDdocDataSequentially = async (
  userNode: TGunUserNode,
  ddocId: string,
  data: Partial<IDdoc>
) => {
  for (const key in data) {
    await putDdocDataByNodeKey(userNode, ddocId, {
      key,
      data: data[key as keyof IDdoc],
    });
  }
};

export const putDdocDataByNodeKey = async (
  userNode: TGunUserNode,
  ddocId: string,
  query: { data: unknown; key: string }
) => {
  // @ts-ignore
  const seaPair = userNode._.sea;

  const { data, key } = query;

  let dataToBeSaved = data;
  if (ENCRYPTED_DDOC_KEYS.includes(key as keyof IDdoc)) {
    dataToBeSaved =
      typeof data === 'object'
        ? await Sea.encrypt(JSON.stringify(data), seaPair)
        : await Sea.encrypt(data, seaPair);
  }
  return new Promise((resolve, reject) => {
    userNode
      .get(GUN_ENV_NAMESPACE_KEY)
      .get(GunNodeKey.Ddoc)
      .get(GunNodeKey.Files)
      .get(ddocId)
      .get(GunNodeKey.FileData)
      .get(query.key)
      .put(dataToBeSaved, (ack: any) => {
        if (!ack.err) {
          resolve(ack);
        } else {
          console.error('Failed to save data', query.key);
          reject({ gunError: ack.err, key: query.key, data: dataToBeSaved });
        }
      });
  });
};

export const getDdocDataByNodeKey = async (
  userNode: TGunUserNode,
  ddocId: string,
  query: { key: string },
  opts?: { supressToast: boolean }
): Promise<any> => {
  //@ts-ignore
  const userSeaPair = userNode._.sea;
  const { key } = query;
  try {
    const data = await waitForGunData(
      userNode
        .get(GUN_ENV_NAMESPACE_KEY)
        .get(GunNodeKey.Ddoc)
        .get(GunNodeKey.Files)
        .get(ddocId)
        .get(GunNodeKey.FileData)
        .get(key)
    );
    if (!data) return null;
    if (ENCRYPTED_DDOC_KEYS.includes(key as keyof IDdoc))
      return await Sea.decrypt(data, userSeaPair);
    return data;
  } catch (error) {
    !opts?.supressToast &&
      toast({
        description: `Failed to load ${query.key}`,
        variant: 'danger',
        hasIcon: true,
      });
    return null;
  }
};
